import React, { useState, useRef } from 'react';
import TomatometerBadge from './TomatometerBadge';

const SWIPE_THRESHOLD = 110; // px to trigger a decision

/**
 * A draggable card showing only anonymized info: genres, Tomatometer, year,
 * runtime/seasons, content rating, a bleeped synopsis and bleeped critic
 * reviews. It NEVER shows the title or poster.
 *
 * onDecision(decision) is called with "like" or "pass".
 */
export default function SwipeCard({ card, onDecision, isTop }) {
  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false });
  const start = useRef(null);

  const runtimeLabel = () => {
    if (card.media_type === 'show') {
      if (card.seasons) return `${card.seasons} season${card.seasons > 1 ? 's' : ''}`;
      return 'TV series';
    }
    return card.runtime ? `${card.runtime} min` : null;
  };

  const onPointerDown = (e) => {
    if (!isTop) return;
    start.current = { x: e.clientX, y: e.clientY };
    setDrag((d) => ({ ...d, dragging: true }));
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!start.current) return;
    setDrag({
      x: e.clientX - start.current.x,
      y: e.clientY - start.current.y,
      dragging: true,
    });
  };

  const endDrag = () => {
    if (!start.current) return;
    const { x } = drag;
    start.current = null;
    if (x > SWIPE_THRESHOLD) {
      onDecision('like');
    } else if (x < -SWIPE_THRESHOLD) {
      onDecision('pass');
    } else {
      setDrag({ x: 0, y: 0, dragging: false });
    }
  };

  const rotation = drag.x / 18;
  const likeOpacity = Math.min(Math.max(drag.x / SWIPE_THRESHOLD, 0), 1);
  const passOpacity = Math.min(Math.max(-drag.x / SWIPE_THRESHOLD, 0), 1);

  const style = isTop
    ? {
        transform: `translate(${drag.x}px, ${drag.y}px) rotate(${rotation}deg)`,
        transition: drag.dragging ? 'none' : 'transform 0.3s ease',
      }
    : undefined;

  return (
    <div
      className={`mp-card ${isTop ? 'mp-card--top' : 'mp-card--back'}`}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {isTop && (
        <>
          <div className="mp-stamp mp-stamp--like" style={{ opacity: likeOpacity }}>
            WATCH
          </div>
          <div className="mp-stamp mp-stamp--pass" style={{ opacity: passOpacity }}>
            SKIP
          </div>
        </>
      )}

      <div className="mp-card-header">
        <span className="mp-media-pill">
          {card.media_type === 'show' ? 'TV Show' : 'Movie'}
        </span>
        <TomatometerBadge score={card.tomatometer} state={card.tomatometer_state} />
      </div>

      <div className="mp-genres">
        {(card.genres || []).map((g) => (
          <span className="mp-genre-chip" key={g}>
            {g}
          </span>
        ))}
      </div>

      <div className="mp-meta-row">
        {card.decade && <span className="mp-meta">{card.decade}</span>}
        {runtimeLabel() && <span className="mp-meta">{runtimeLabel()}</span>}
        {card.content_rating && (
          <span className="mp-meta mp-meta--rating">{card.content_rating}</span>
        )}
      </div>

      <div className="mp-reviews">
        <div className="mp-reviews-title">Critics say</div>
        {(card.reviews || []).length === 0 && (
          <p className="mp-no-reviews">No critic reviews available.</p>
        )}
        {(card.reviews || []).map((r, i) => (
          <blockquote
            key={i}
            className={`mp-review ${
              r.fresh === true
                ? 'mp-review--fresh'
                : r.fresh === false
                ? 'mp-review--rotten'
                : ''
            }`}
          >
            <p className="mp-review-quote">“{r.quote_bleeped}”</p>
            <footer className="mp-review-byline">
              {r.critic_name || 'Critic'}
              {r.publication ? `, ${r.publication}` : ''}
              {r.top_critic ? ' · Top Critic' : ''}
            </footer>
          </blockquote>
        ))}
      </div>
    </div>
  );
}
